/**
 * BT Product Video — Autoplay AFTER full page load (no intent gate)
 * + Center play/pause overlay ONLY if autoplay is blocked
 *
 * - Poster is LCP.
 * - Video is mounted after window.load (keeps LCP poster-based).
 * - Then we force load() + play().
 * - If autoplay is blocked, we show a centered overlay play/pause button.
 * - Carousel: mount/play selected; pause non-selected.
 */
(function () {
  // -----------------------------
  // Helpers
  // -----------------------------
  function isMobile() {
    return window.matchMedia && window.matchMedia("(max-width: 749px)").matches;
  }

  function qsa(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  function isInSelectedSlide(el) {
    var slide = el && el.closest && el.closest(".Product__SlideItem");
    return !!(slide && slide.classList.contains("is-selected"));
  }

  function withRaf(fn) {
    if ("requestAnimationFrame" in window) requestAnimationFrame(fn);
    else setTimeout(fn, 0);
  }

  function getPlayIcon() {
    return (
      '<svg viewBox="0 0 24 24" aria-hidden="true">' +
      '<path d="M8 5v14l11-7z"></path>' +
      "</svg>"
    );
  }

  function getPauseIcon() {
    return (
      '<svg viewBox="0 0 24 24" aria-hidden="true">' +
      '<path d="M6 5h4v14H6zM14 5h4v14h-4z"></path>' +
      "</svg>"
    );
  }

  function safePlay(video) {
    var p;
    try {
      p = video.play();
    } catch (e) {
      return null;
    }
    if (p && typeof p.catch === "function") p.catch(function () {});
    return p;
  }

  function showCenterBtn(btn) {
    if (!btn) return;
    btn.classList.add("is-visible");
  }

  function hideCenterBtn(btn) {
    if (!btn) return;
    btn.classList.remove("is-visible");
  }

  function syncCenterBtn(btn, video) {
    if (!btn || !video) return;
    if (video.paused) {
      btn.setAttribute("aria-label", "Play video");
      btn.innerHTML = getPlayIcon();
    } else {
      btn.setAttribute("aria-label", "Pause video");
      btn.innerHTML = getPauseIcon();
    }
  }

  function ensureCenterBtn(swap, video) {
    if (!swap || !video) return null;

    var btn = swap.querySelector(".BT_VideoSwap__centerBtn");
    if (!btn) {
      btn = document.createElement("button");
      btn.type = "button";
      btn.className = "BT_VideoSwap__centerBtn";
      btn.setAttribute("aria-label", "Play video");
      btn.innerHTML = getPlayIcon();

      // Toggle play/pause
      btn.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();

        if (video.paused) {
          safePlay(video);
        } else {
          try {
            video.pause();
          } catch (e) {}
        }
        syncCenterBtn(btn, video);

        // Hide while playing (premium feel). Button reappears on pause.
        if (!video.paused) hideCenterBtn(btn);
        else showCenterBtn(btn);
      });

      swap.appendChild(btn);
    }

    // Keep button state in sync
    video.addEventListener(
      "play",
      function () {
        syncCenterBtn(btn, video);
        hideCenterBtn(btn);
      },
      { passive: true }
    );
    video.addEventListener(
      "pause",
      function () {
        syncCenterBtn(btn, video);
        showCenterBtn(btn);
      },
      { passive: true }
    );

    return btn;
  }

  // -----------------------------
  // Core: mount + playback
  // -----------------------------
  function mountInto(swap) {
    if (!swap || swap.dataset.mounted === "1") return swap._btVideo || null;

    var tpl = swap.querySelector(".BT_VideoSwap__tpl");
    var mount = swap.querySelector(".BT_VideoSwap__mount");
    if (!tpl || !mount) return null;

    mount.innerHTML = tpl.innerHTML;

    var video = mount.querySelector("video");
    if (!video) return null;

    // Autoplay prerequisites
    video.muted = true;
    video.setAttribute("muted", "");
    video.playsInline = true;
    video.setAttribute("playsinline", "");

    // Controls hidden by default (we'll show overlay only on fallback)
    video.controls = false;
    video.removeAttribute("controls");

    // After load we actually want it to fetch & play
    try {
      video.preload = "auto";
      video.setAttribute("preload", "auto");
    } catch (e) {}

    // Fade in once actually playing (your CSS handles is-ready)
    function ready() {
      swap.classList.add("is-ready");
    }
    video.addEventListener("playing", ready, { once: true });

    // Hover controls on desktop (optional UX you had)
    var pinned = false;

    function showControls() {
      if (pinned) return;
      video.controls = true;
      video.setAttribute("controls", "");
    }
    function hideControls() {
      if (pinned) return;
      video.controls = false;
      video.removeAttribute("controls");
    }

    if (!isMobile()) {
      swap.addEventListener("mouseenter", showControls);
      swap.addEventListener("mouseleave", function () {
        setTimeout(function () {
          if (!swap.matches(":hover")) hideControls();
        }, 120);
      });

      // Click toggles pinned controls
      video.addEventListener("click", function () {
        pinned = !pinned;
        if (pinned) {
          video.controls = true;
          video.setAttribute("controls", "");
        } else {
          video.controls = false;
          video.removeAttribute("controls");
        }
      });
    } else {
      // Mobile: first tap shows controls (optional)
      swap.addEventListener(
        "pointerdown",
        function () {
          pinned = true;
          video.controls = true;
          video.setAttribute("controls", "");
        },
        { passive: true, once: true }
      );
    }

    swap.dataset.mounted = "1";
    swap._btVideo = video;

    return video;
  }

  function forceLoadAndPlay(swap, video) {
    if (!video) return;

    // Ensure autoplay conditions
    video.muted = true;
    video.setAttribute("muted", "");
    video.playsInline = true;
    video.setAttribute("playsinline", "");

    // Force source load (helps Safari / some theme outputs)
    try {
      video.load();
    } catch (e) {}

    var p;
    try {
      p = video.play();
    } catch (e) {
      p = null;
    }

    // If autoplay blocked → show centered overlay play button
    if (p && typeof p.catch === "function") {
      p.catch(function () {
        var btn = ensureCenterBtn(swap, video);
        syncCenterBtn(btn, video);
        showCenterBtn(btn);

        // Optional: also show native controls when blocked
        // video.controls = true;
        // video.setAttribute("controls", "");
      });
    }
  }

  // -----------------------------
  // Hero selection
  // -----------------------------
  function getHeroSwap() {
    return (
      document.querySelector(".BT_VideoSwap.is-initial") ||
      document.querySelector(".Product__SlideItem.is-selected .BT_VideoSwap") ||
      document.querySelector(".BT_VideoSwap")
    );
  }

  function mountAndPlayHero() {
    var hero = getHeroSwap();
    if (!hero) return;

    var v = mountInto(hero);
    if (v) forceLoadAndPlay(hero, v);
  }

  // -----------------------------
  // Carousel bindings
  // -----------------------------
  function mountSelectedInCarousel() {
    qsa(".BT_VideoSwap").forEach(function (sw) {
      if (!isInSelectedSlide(sw)) return;
      var v = mountInto(sw);
      if (v) forceLoadAndPlay(sw, v);
    });
  }

  function pauseNonSelectedInCarousel() {
    qsa(".BT_VideoSwap").forEach(function (sw) {
      if (isInSelectedSlide(sw)) return;
      var v = sw._btVideo || sw.querySelector(".BT_VideoSwap__mount video");
      if (!v) return;
      try {
        v.pause();
      } catch (e) {}
    });
  }

  function bindFlickity() {
    var el =
      document.querySelector(".Product__Slideshow.Carousel") ||
      document.querySelector(".Product__Slideshow");
    if (!el) return false;

    var flkty = null;
    if (el.flickity && el.flickity.on) flkty = el.flickity;
    else if (window.Flickity && window.Flickity.data) flkty = window.Flickity.data(el);

    if (!flkty || !flkty.on) return false;

    flkty.on("select", function () {
      withRaf(function () {
        mountSelectedInCarousel();
        pauseNonSelectedInCarousel();
      });
    });

    return true;
  }

  function bindObserverFallback() {
    var root = document.querySelector(".Product__Slideshow") || document.body;
    var mo = new MutationObserver(function () {
      withRaf(function () {
        mountSelectedInCarousel();
        pauseNonSelectedInCarousel();
      });
    });
    mo.observe(root, { subtree: true, attributes: true, attributeFilter: ["class"] });
  }

  // -----------------------------
  // Init after full load
  // -----------------------------
  function init() {
    // After full load, mount & play hero (small delay = stable layout)
    setTimeout(function () {
      mountAndPlayHero();
    }, 150);

    var ok = bindFlickity();
    if (!ok) bindObserverFallback();
  }

  window.addEventListener(
    "load",
    function () {
      init();
    },
    { once: true }
  );
})();